/**
 * Service Worker Auth Bridge
 *
 * Caches the current Supabase access token in IndexedDB so the
 * service worker can authenticate background ACK requests
 * (ack-push-receipt). Service workers cannot read localStorage,
 * so this is the safest way to give them a short-lived JWT.
 *
 * SECURITY: only the access token is stored (never the refresh
 * token). It is cleared on sign-out.
 */

const DB_NAME = 'lupuslogix_device_store';
const STORE_NAME = 'device_info';
const AUTH_TOKEN_KEY = 'lupuslogix_auth_token';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

export async function setSwAuthToken(token: string | null): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = token
        ? store.put(token, AUTH_TOKEN_KEY)
        : store.delete(AUTH_TOKEN_KEY);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch (err) {
    // Non-fatal — push ACKs will simply be skipped until next refresh.
    console.warn('[swAuthBridge] Failed to sync auth token to IndexedDB:', err);
  }
}

export async function clearSwAuthToken(): Promise<void> {
  return setSwAuthToken(null);
}